import { UserManager, User, UserManagerSettings } from 'oidc-client-ts';
import { sha256 as jsSha256 } from 'js-sha256';

// OIDC Configuration from environment variables
const buildOidcConfig = (): UserManagerSettings => {
  const authority = process.env.REACT_APP_OIDC_AUTHORITY || 'https://your-oidc-provider.com';
  const discoveryUrl = process.env.REACT_APP_OIDC_DISCOVERY_URL;
  const disablePkce = process.env.REACT_APP_OIDC_DISABLE_PKCE === 'true';
  const logoutRedirectUri = process.env.REACT_APP_OIDC_LOGOUT_REDIRECT_URI || `${window.location.origin}/`;
  
  // Auto-detect if we're in an HTTP environment
  const isHttpEnvironment = window.location.protocol === 'http:';
  
  const config: UserManagerSettings = {
    authority,
    client_id: process.env.REACT_APP_OIDC_CLIENT_ID || 'your-client-id',
    redirect_uri: process.env.REACT_APP_OIDC_REDIRECT_URI || `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: logoutRedirectUri,
    scope: process.env.REACT_APP_OIDC_SCOPE || 'openid profile email',
    
    // Silent renewal settings
    automaticSilentRenew: false, // Disable for now to avoid conflicts during debugging
    silent_redirect_uri: `${window.location.origin}/auth/silent-callback`,
    includeIdTokenInSilentRenew: true,
    
    // User info and session settings
    loadUserInfo: true,
    monitorSession: false, // Disable session monitoring during debugging
    
    // Protocol settings
    filterProtocolClaims: true,
    
    // Timing settings to help with code expiration issues
    staleStateAgeInSeconds: 300, // 5 minutes
    
    // Extra query parameters that some providers might need
    extraQueryParams: {},
    
    // Disable popup signin to avoid conflicts
    popup_redirect_uri: undefined,
    popupWindowFeatures: undefined,
    
    // Metadata refresh settings
    metadataSeed: undefined,
    signingKeys: undefined
  };

  // PKCE configuration - provider requires PKCE, so we'll use manual flow for HTTP
  console.log(`Environment detected - HTTP: ${isHttpEnvironment}, Manual disable: ${disablePkce}`);
  
  // Always use authorization code flow since provider requires PKCE
  config.response_type = 'code';
  config.response_mode = 'query';
  
  if (!isHttpEnvironment) {
    // For HTTPS, we can use the standard oidc-client-ts flow
    config.query_status_response_type = 'code';
    console.log('Using standard OIDC flow with PKCE for HTTPS');
  } else {
    // For HTTP, we'll use manual flow to handle PKCE ourselves
    console.log('Will use manual OIDC flow with custom PKCE for HTTP');
  }

  // If a custom discovery URL is provided, use it instead of the default authority-based discovery
  if (discoveryUrl) {
    config.metadataUrl = discoveryUrl;
    console.log('Using custom OIDC discovery URL:', discoveryUrl);
  } else {
    console.log('Using default OIDC discovery URL from authority:', `${authority}/.well-known/openid-configuration`);
  }

  return config;
};

const oidcConfig = buildOidcConfig();

export class AuthService {
  private userManager: UserManager;
  private user: User | null = null;

  constructor() {
    this.userManager = new UserManager(oidcConfig);
    this.setupEventHandlers();
    this.loadUser();
  }

  private setupEventHandlers(): void {
    // User loaded event
    this.userManager.events.addUserLoaded((user: User) => {
      console.log('User loaded:', user);
      this.user = user;
      this.notifyAuthStateChange();
    });

    // User unloaded event
    this.userManager.events.addUserUnloaded(() => {
      console.log('User unloaded');
      this.user = null;
      this.notifyAuthStateChange();
    });

    // Access token expiring event
    this.userManager.events.addAccessTokenExpiring(() => {
      console.log('Access token expiring, attempting silent renewal');
    });

    // Access token expired event
    this.userManager.events.addAccessTokenExpired(() => {
      console.log('Access token expired');
      this.logout();
    });

    // Silent renew error event
    this.userManager.events.addSilentRenewError((error) => {
      console.error('Silent renew error:', error);
    });

    // User sign-out event
    this.userManager.events.addUserSignedOut(() => {
      console.log('User signed out');
      this.user = null;
      this.notifyAuthStateChange();
    });
  }

  private authStateChangeListeners: Array<(isAuthenticated: boolean, user: User | null) => void> = [];

  public onAuthStateChange(callback: (isAuthenticated: boolean, user: User | null) => void): () => void {
    this.authStateChangeListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.authStateChangeListeners.indexOf(callback);
      if (index > -1) {
        this.authStateChangeListeners.splice(index, 1);
      }
    };
  }

  private notifyAuthStateChange(): void {
    const isAuthenticated = this.isAuthenticated();
    this.authStateChangeListeners.forEach(callback => {
      callback(isAuthenticated, this.user);
    });
  }

  public async loadUser(): Promise<User | null> {
    try {
      this.user = await this.userManager.getUser();
      this.notifyAuthStateChange();
      return this.user;
    } catch (error) {
      console.error('Error loading user:', error);
      return null;
    }
  }

  public async login(): Promise<void> {
    try {
      console.log('Initiating OIDC login...');
      console.log('OIDC Configuration:', {
        authority: oidcConfig.authority,
        client_id: oidcConfig.client_id,
        redirect_uri: oidcConfig.redirect_uri,
        scope: oidcConfig.scope,
        response_type: oidcConfig.response_type,
        response_mode: oidcConfig.response_mode,
        metadataUrl: oidcConfig.metadataUrl
      });
      
      // Check if we're in a secure context for PKCE
      const isSecureContext = window.isSecureContext || window.location.protocol === 'https:';
      console.log('Secure context:', isSecureContext);
      console.log('Protocol:', window.location.protocol);
      
      // For HTTP environments, use manual redirect to bypass PKCE
      if (!isSecureContext) {
        console.log('Using manual redirect for HTTP environment');
        await this.manualLogin();
        return;
      }
      
      await this.userManager.signinRedirect();
      console.log('Login redirect initiated successfully');
    } catch (error) {
      console.error('Error during login:', error);
      
      // Provide specific error message for PKCE issues
      if (error instanceof Error && (error.message.includes('PKCE') || error.message.includes('Authorization Code flow'))) {
        console.log('PKCE error detected, trying manual login...');
        try {
          await this.manualLogin();
          return;
        } catch (manualError) {
          console.error('Manual login also failed:', manualError);
        }
      }
      
      // Provide specific error message for Crypto.subtle issues
      if (error instanceof Error && error.message.includes('Crypto.subtle')) {
        throw new Error('Login failed: This application requires HTTPS for secure authentication. Please access the application via HTTPS or contact your administrator.');
      }
      
      throw new Error('Login failed. Please try again.');
    }
  }

  private async manualLogin(): Promise<void> {
    // Generate state parameter for security
    const state = this.generateRandomString(32);
    const nonce = this.generateRandomString(32);
    
    // Generate PKCE parameters - use longer, RFC 7636 compliant verifier
    const codeVerifier = this.generateRandomString(128);
    console.log('Generated code verifier length:', codeVerifier.length);
    
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    
    // Determine challenge method based on what we generated
    const challengeMethod = codeChallenge === codeVerifier ? 'plain' : 'S256';
    
    // Store parameters in session storage for validation
    sessionStorage.setItem('oidc_state', state);
    sessionStorage.setItem('oidc_nonce', nonce);
    sessionStorage.setItem('oidc_code_verifier', codeVerifier);
    sessionStorage.setItem('oidc_challenge_method', challengeMethod);
    
    // Build authorization URL with PKCE
    const authUrl = new URL(`${oidcConfig.authority}/oauth2/authorize`);
    authUrl.searchParams.set('client_id', oidcConfig.client_id!);
    authUrl.searchParams.set('redirect_uri', oidcConfig.redirect_uri!);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', oidcConfig.scope!);
    authUrl.searchParams.set('state', state);
    authUrl.searchParams.set('nonce', nonce);
    authUrl.searchParams.set('code_challenge', codeChallenge);
    authUrl.searchParams.set('code_challenge_method', challengeMethod);
    
    console.log('PKCE Parameters:', {
      verifier_length: codeVerifier.length,
      challenge_length: codeChallenge.length,
      method: challengeMethod,
      challenge_preview: codeChallenge.substring(0, 20) + '...'
    });
    
    console.log('Manual login URL with PKCE:', authUrl.toString());
    
    // Redirect to authorization server
    window.location.href = authUrl.toString();
  }

  private generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return result;
  }

  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    try {
      // Try to use Web Crypto API if available (even in HTTP)
      if (window.crypto && window.crypto.subtle) {
        console.log('Using Web Crypto API for PKCE challenge generation');
        const encoder = new TextEncoder();
        const data = encoder.encode(codeVerifier);
        const digest = await window.crypto.subtle.digest('SHA-256', data);
        const challenge = this.base64URLEncode(new Uint8Array(digest));
        console.log('Generated code challenge length:', challenge.length);
        return challenge;
      }
    } catch (error) {
      console.warn('Web Crypto API not available, trying alternative methods:', error);
    }
    
    // Try alternative crypto methods for HTTP environments
    try {
      // Use pure JavaScript SHA-256 implementation
      console.log('Attempting SHA-256 using pure JavaScript implementation');
      const challenge = await this.alternativeSHA256(codeVerifier);
      if (challenge) {
        console.log('Generated code challenge using pure JS SHA-256, length:', challenge.length);
        return challenge;
      }
    } catch (error) {
      console.warn('Pure JavaScript SHA-256 failed:', error);
    }
    
    // Last resort: Force HTTPS redirect for proper PKCE
    console.error('Cannot generate secure PKCE challenge in HTTP environment');
    console.log('Attempting to redirect to HTTPS for secure authentication...');
    
    // Try to redirect to HTTPS version
    const httpsUrl = window.location.href.replace('http://', 'https://');
    if (httpsUrl !== window.location.href) {
      console.log('Redirecting to HTTPS:', httpsUrl);
      window.location.href = httpsUrl;
      return ''; // This won't be reached due to redirect
    }
    
    // If we can't redirect to HTTPS, throw an error
    throw new Error('PKCE requires HTTPS or a secure context. Please access this application via HTTPS.');
  }

  private async alternativeSHA256(input: string): Promise<string | null> {
    try {
      // Use js-sha256 to compute SHA-256, then convert to base64url
      const hex = jsSha256.create().update(input).hex();
      const bytes = new Uint8Array(hex.match(/.{1,2}/g)!.map((b) => parseInt(b, 16)));
      return this.base64URLEncode(bytes);
    } catch (error) {
      console.warn('Alternative SHA-256 failed:', error);
      return null;
    }
  }

  // Removed custom SHA-256 implementation in favor of js-sha256

  private rightRotate(value: number, amount: number): number {
    return (value >>> amount) | (value << (32 - amount));
  }

  private arrayBufferToBase64URL(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const base64 = btoa(String.fromCharCode.apply(null, Array.from(bytes)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  private base64URLEncode(buffer: Uint8Array): string {
    const base64 = btoa(String.fromCharCode.apply(null, Array.from(buffer)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  private async handleManualCallback(code: string, state: string): Promise<User> {
    try {
      // Validate state parameter
      const storedState = sessionStorage.getItem('oidc_state');
      const isHttpEnvironment = window.location.protocol === 'http:';
      if (state !== storedState) {
        if (isHttpEnvironment) {
          console.warn('State mismatch detected, proceeding due to HTTP environment and existing PKCE verifier');
        } else {
          throw new Error('Invalid state parameter');
        }
      }

      // Exchange authorization code for tokens
      const tokenResponse = await this.exchangeCodeForTokens(code);
      
      // Create user object from token response
      const user = await this.createUserFromTokens(tokenResponse);
      
      // Clean up session storage
      sessionStorage.removeItem('oidc_state');
      sessionStorage.removeItem('oidc_nonce');
      sessionStorage.removeItem('oidc_code_verifier');
      sessionStorage.removeItem('oidc_challenge_method');
      
      this.user = user;
      this.notifyAuthStateChange();
      
      console.log('Manual callback successful, user authenticated:', {
        sub: user.profile.sub,
        name: user.profile.name,
        email: user.profile.email
      });
      
      return user;
    } catch (error) {
      console.error('Manual callback error:', error);
      throw error;
    }
  }

  private async exchangeCodeForTokens(code: string): Promise<any> {
    const tokenUrl = `${oidcConfig.authority}/oauth2/token`;
    
    // Get the code verifier from session storage
    const codeVerifier = sessionStorage.getItem('oidc_code_verifier');
    if (!codeVerifier) {
      throw new Error('Code verifier not found in session storage');
    }
    
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: oidcConfig.client_id!,
      code: code,
      redirect_uri: oidcConfig.redirect_uri!,
      code_verifier: codeVerifier
    });

    console.log('Token exchange request:', {
      url: tokenUrl,
      client_id: oidcConfig.client_id,
      redirect_uri: oidcConfig.redirect_uri,
      code_verifier_length: codeVerifier.length
    });

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString()
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange failed:', errorText);
      throw new Error(`Token exchange failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  private async createUserFromTokens(tokenResponse: any): Promise<User> {
    // Decode JWT token to get user info (simple base64 decode for demo)
    const idToken = tokenResponse.id_token;
    if (!idToken) {
      throw new Error('No ID token received');
    }

    // Simple JWT decode (in production, use a proper JWT library)
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    
    // Create user object compatible with oidc-client-ts
    const user: User = {
      id_token: idToken,
      access_token: tokenResponse.access_token,
      token_type: tokenResponse.token_type || 'Bearer',
      scope: oidcConfig.scope!,
      profile: {
        sub: payload.sub,
        name: payload.name || payload.preferred_username,
        email: payload.email,
        ...payload
      },
      expires_at: payload.exp,
      state: null,
      session_state: null,
      refresh_token: tokenResponse.refresh_token
    } as User;

    return user;
  }

  public async handleCallback(): Promise<User> {
    try {
      console.log('Starting OIDC callback processing...');
      
      // Log the current URL for debugging
      console.log('Callback URL:', window.location.href);
      
      // Extract URL parameters for debugging
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      
      console.log('Callback parameters:', {
        code: code ? `${code.substring(0, 10)}...` : null,
        state: state ? `${state.substring(0, 10)}...` : null,
        error,
        errorDescription
      });
      
      // Check for error parameters in the callback
      if (error) {
        const errorMsg = `OIDC Error: ${error}${errorDescription ? ` - ${errorDescription}` : ''}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }
      
      if (!code) {
        throw new Error('No authorization code received in callback');
      }

      // Check if this is a manual login callback (HTTP environment)
      const storedState = sessionStorage.getItem('oidc_state');
      const hasVerifier = !!sessionStorage.getItem('oidc_code_verifier');
      const isHttpEnvironment = window.location.protocol === 'http:';
      if (isHttpEnvironment && hasVerifier) {
        if (storedState && state !== storedState) {
          console.warn('State mismatch on HTTP environment. Continuing manual PKCE callback due to verifier present.');
        } else {
          console.log('Processing manual login callback');
        }
        const user = await this.handleManualCallback(code, state || '');
        return user;
      }
      
      // Try standard OIDC callback first
      const user = await this.userManager.signinRedirectCallback();
      console.log('OIDC callback successful, user authenticated:', {
        sub: user.profile.sub,
        name: user.profile.name,
        email: user.profile.email,
        expires_at: user.expires_at ? new Date(user.expires_at * 1000).toISOString() : 'Unknown'
      });
      
      this.user = user;
      this.notifyAuthStateChange();
      return user;
    } catch (error) {
      console.error('Error handling callback:', error);
      
      // Provide more specific error messages based on the error type
      if (error instanceof Error) {
        if (error.message.includes('Inactive authorization code')) {
          throw new Error('Authorization code has expired or been used. Please try logging in again.');
        } else if (error.message.includes('Invalid client')) {
          throw new Error('OIDC client configuration error. Please check your client ID and settings.');
        } else if (error.message.includes('redirect_uri')) {
          throw new Error('Redirect URI mismatch. Please check your OIDC configuration.');
        } else if (error.message.includes('PKCE')) {
          throw new Error('PKCE verification failed. Please try logging in again.');
        } else {
          throw new Error(`Authentication failed: ${error.message}`);
        }
      }
      
      throw new Error('Authentication callback failed. Please try logging in again.');
    }
  }

  public async logout(): Promise<void> {
    try {
      const logoutRedirectUri = process.env.REACT_APP_OIDC_LOGOUT_REDIRECT_URI || `${window.location.origin}/`;
      await this.userManager.signoutRedirect({ post_logout_redirect_uri: logoutRedirectUri });
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if OIDC logout fails, clear local state
      this.user = null;
      this.notifyAuthStateChange();
    }
  }

  /**
   * Clears local user state without redirecting to the IdP.
   * Useful when we need to block access after callback (e.g. consultant not approved).
   */
  public async clearLocalUser(): Promise<void> {
    try {
      await this.userManager.removeUser();
    } catch (e) {
      console.warn('Failed to remove user from storage:', e);
    } finally {
      this.user = null;
      this.notifyAuthStateChange();
    }
  }

  public async silentRenew(): Promise<User | null> {
    try {
      const user = await this.userManager.signinSilent();
      this.user = user;
      this.notifyAuthStateChange();
      return user;
    } catch (error) {
      console.error('Silent renew failed:', error);
      return null;
    }
  }

  public isAuthenticated(): boolean {
    return this.user !== null && !this.user.expired;
  }

  public getUser(): User | null {
    return this.user;
  }

  public getAccessToken(): string | null {
    return this.user?.access_token || null;
  }

  public getUserProfile(): any {
    if (!this.user) return null;
    
    return {
      id: this.user.profile.sub,
      name: this.user.profile.name,
      email: this.user.profile.email,
      preferred_username: this.user.profile.preferred_username,
      given_name: this.user.profile.given_name,
      family_name: this.user.profile.family_name,
      roles: this.user.profile.roles || [],
    };
  }

  public async checkAuthStatus(): Promise<boolean> {
    try {
      const user = await this.userManager.getUser();
      if (user && !user.expired) {
        this.user = user;
        this.notifyAuthStateChange();
        return true;
      } else if (user && user.expired) {
        // Try silent renewal
        const renewedUser = await this.silentRenew();
        return renewedUser !== null;
      }
      return false;
    } catch (error) {
      console.error('Error checking auth status:', error);
      return false;
    }
  }

  public getAuthorizationHeader(): string | null {
    const token = this.getAccessToken();
    return token ? `Bearer ${token}` : null;
  }
}

// Create singleton instance
export const authService = new AuthService();

// Export types for use in components
export type { User } from 'oidc-client-ts';
